
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import {
  commonModule,
  companyModule,
  installationsModule,
  itemsModule,
  tasksModule,
} from "@/store/modules/store-accessor";
import {
  taskStatusToColor,
  taskStatusToName,
  formatOnlyDate,
  taskColor,
  getOptimalTextColor,
  templatesFilenameLanguage,
  formatDateWithoutSeconds,
  priorityIcon,
  pagesExportLimit,
} from "@/helpers/data";
import { TimeUtil } from "@/helpers/TimeUtil";
import dayjs from "dayjs";
import FileSaver from "file-saver";
import SnoozeTask from "@/components/Snooze/SnoozeTask.vue";
import ExportMenu from "@/components/ExportMenu/ExportMenu.vue";

@Component({
  components: {
    SnoozeTask,
    ExportMenu
  }
})
export default class InstallationsSideviewTasksTab extends Vue {
  @Prop() installation: Record<string, any>;
  @Prop() taskId: number;

  expansionTaskIdIndex = null as number | null;
  expansionPendingTaskIdIndex = null as number | null;
  isExpanded = false;

  isDownloadingTaskPdf = false;
  isDownloadingExport = false;

  tableOptions = {sortBy: ['task_status_id', 'due_at'], sortDesc: [false, true], itemsPerPage: 10, page: 1};
  expandedItems: any[] = [];

  tableheaders: any [] = [
    { class:'font-weight-black', text: this.$t("priority"), sortable: false, value: 'priority_level'},
    { class:'font-weight-black', text: this.$t("title"), sortable: false, value: 'title'},
    { class:'font-weight-black', text: this.$t("item"), sortable: false, value: 'installation_item_id' },
    { class:'font-weight-black', text: this.$t("action"), sortable: false, value: 'action_type_text' },
    { class:'font-weight-black', text: this.$t("userPartner"), sortable: false, value: 'assignee_id' },
    { class:'font-weight-black', text: this.$t("due_at"), sortable: true, value: 'due_at' },
    { class:'font-weight-black', text: this.$t("status"), sortable: true, value: 'task_status_id' },
    { class:'', text: '', value: 'is_periodic', width: '85px', sortable: false, align: 'right' }, // Move expand arrow to the right
    { class:'', text: '', value: 'data-table-expand' }, // Move expand arrow to the right
  ]

  mounted() {
    commonModule.getPersonnelCombo();
    commonModule.getPersonnelPartnersCombo();
    commonModule.getActionCombo();
    commonModule.getDurationCombo();
    itemsModule.getAllCompanyPrimaryInventoryItems();
  }

  updated() {
    if (this.tasks.length > 0 && this.taskId && !this.isExpanded) {
      this.showExpansion();
      this.isExpanded = true;
    }
  }

  showExpansion() {
    const expansionTask = this.tasks?.find(i => i.id == this.taskId);

    if (expansionTask) {
      const sortBy = this.tableOptions.sortBy;
      const sortDesc = this.tableOptions.sortDesc;

      // If sorting in tableOptions changes, so must the following sorting code
      const sortedTasks = [...this.tasks].sort((a: any, b: any) => {
        for (let i = 0; i < sortBy.length; i++) {
          const key = sortBy[i];
          const desc = sortDesc[i];

          const aVal = a[key];
          const bVal = b[key];

          if (aVal < bVal) return desc ? 1 : -1;
          if (aVal > bVal) return desc ? -1 : 1;
        }
        return 0;
      });

      const taskIndex = sortedTasks?.findIndex(t => t.id == this.taskId);
      const page = Math.floor(taskIndex / this.tableOptions.itemsPerPage) + 1;
      this.tableOptions.page = page;

      this.expandedItems = [expansionTask];
    }
  }

  @Watch("installation.info.id")
  updateTasks() {
    commonModule.getPersonnelCombo();
    commonModule.getActionCombo();
    commonModule.getDurationCombo();
  }

  get tasks() {
    if(!installationsModule.currentInstallation.tasks) return [];
    return installationsModule.currentInstallation.tasks;
  }

  formatTaskDate(date: Date, all_day: string) {
    if (!date) {
      return "-";
    }
    else {
      if (all_day) {
        return formatOnlyDate(date);
      }
      return formatDateWithoutSeconds(date);
    }
  }

  formatDateTime(date: Date): string {
    return TimeUtil.formatDateTime(date);
  }

  taskIsComplete(task: any) {
    if (task.task_status_id == 1) return true;
    else return false;
  }

  dateToColor(task: Record<string, any>) {
    return taskColor(task);
  }

  optimalTextColor(color: string) {
    return getOptimalTextColor(color);
  }

  statusToName(status: number) {
    return taskStatusToName(status);
  }

  statusToColor(status: number) {
    return taskStatusToColor(status);
  }

  addTask() {
    commonModule.showModal({
      name: "add-task-modal",
      payload: { installation: this.installation },
    });
  }

  editTask(task: any) {
    commonModule.showModal({
      name: "edit-task-modal",
      payload: { task: task },
    });
  }

  completeTask(task: any) {
    commonModule.showModal({
      name: "complete-task-modal",
      payload: { task: task },
    });
  }

  completeTaskWithAppointment(task: any) {
    commonModule.showModal({
      name: "add-appointment-modal",
      payload: {
        task_id: task.id,
        installation: this.installation,
        action_type_id: task.action_type_id,
        assignee_id: task.assignee_id,
        notes: task.notes,
        job_item: task.task_item,
        installation_item_id: task.installation_item_id,
        item_type_text: task.item_type_text,
        item_make: task.item_make,
        item_model: task.item_model,
        item_serial_no: task.item_serial_no,
        assignees:task.assignees,
      },
    });
  }

  completeTaskWithJob(task: any) {
    commonModule.showModal({
      name: "add-job-modal",
      payload: {
        task_id: task.id,
        installation: this.installation,
        action_type_id: task.action_type_id,
        assignee: task.assignee_id,
        notes: task.notes,
        job_item: task.task_item,
        installation_item_id: task.installation_item_id,
        item_type_text: task.item_type_text,
        item_make: task.item_make,
        item_model: task.item_model,
        item_serial_no: task.item_serial_no,
      },
    });
  }

  deleteTask(task: any) {
    commonModule.showModal({
      name: "delete-task-modal",
      payload: { task: task },
    });
  }

  downloadExportLengthCheck(type: string) {
    if (type === 'pdf') {
      return this.tasks?.length > 0 && this.tasks?.length <= pagesExportLimit();
    }
    return this.tasks?.length > 0;
  }

  async donwloadTasksExport(format: string) {
    this.isDownloadingExport = true;
    const data = {
      user_timezone: dayjs.tz.guess(),
      language: localStorage.getItem("lang"),
      format,
      installation_id: this.installation.info.id,
    };

    try {
      const company = await companyModule.getCompanyInfo();
      const filetype = format === 'excel' ? 'xlsx' : 'pdf';
      const filename = `${templatesFilenameLanguage()}_${company.entity_name}_DfB_${data.installation_id}_tasks_${dayjs().format('YYYY_MM_DD_HH_mm_ss')}.${filetype}`.replace(/ /g, "_");
      const exportFile = await installationsModule.getInstallationSideviewTasksExport(data);
      const file = new File([exportFile], filename);
      FileSaver.saveAs(file);
    } finally {
      this.isDownloadingExport = false;
    }
  }

  async donwloadTaskPdf(task: Record<string, unknown>) {
    this.isDownloadingTaskPdf = true;
    const userTimezone = dayjs.tz.guess();

    const data = {
      taskId: task.id,
      user_timezone: userTimezone,
      language: localStorage.getItem("lang"),
    };

    try {
      if (task.id) {
        const company = await companyModule.getCompanyInfo();
        const filename = `${templatesFilenameLanguage()}_${company.entity_name}_${this.installation.info.tag_id}_task_${task.id}.pdf`.replace(/ /g, "_");
        const pdf = await tasksModule.getTaskPdfExportAction(data);
        const file = new File([pdf], filename);
        FileSaver.saveAs(file);
      }
    } finally {
      this.isDownloadingTaskPdf = false;
    }
  }

  showReminderLabel(task: Record<string, any>) {
    return task.title && task.title.length > 0
      ? task.title
      : task.category && task.category.includes(1)
      ? this.$i18n.t("reminder_label")
      : "-";
  }

  itemText(item: Record<string, any>) {
    const installationItemDescriptionIsEnabled = (companyModule.company?.preferences as any)?.installation_item_description;

    if(installationItemDescriptionIsEnabled) {
      const itemDescription = item.item_description && item.item_description.length > 0 ? item.item_description : '';
      return item.task_item && item.task_item.length > 0 ? `${itemDescription}${itemDescription ? ' | ' : ''}${item.task_item}` : '-'
    }

    return  item.task_item && item.task_item.length > 0 ? item.task_item : '-'
  }

  showCompleteTaskWithAppointmentOption(task: Record<string, any>) {
    if (!Array.isArray(task.assignees)) {
        return false;
    }

    const partnersIds = commonModule.personnelPartnersCombo.filter(partner => partner.is_partner).map(partner => partner.id);

    return task.assignees.some((assignee: Record<string, any>) => !partnersIds.includes(assignee.id));
  }

  priorityIcon(name: string) {
    return priorityIcon(name);
  }

  get tableIsLoading() {
    return installationsModule.sideviewTableIsLoading;
  }

}
